/**
 * Typed, validated environment variables for unity/base.
 *
 * All access to process.env in application code (non-test) must go through
 * this module. Validation runs at server startup — missing required vars
 * throw immediately with a clear error, never silently as undefined.
 *
 * Skip validation in test environments (NODE_ENV=test) so vitest suites that
 * manipulate process.env directly continue to work without bootstrapping all
 * required vars.
 *
 * Client-accessible vars (NEXT_PUBLIC_*) are available in both server and
 * client bundles. All other vars are server-only.
 */

import { z } from 'zod'

// ─── Schema ──────────────────────────────────────────────────────────────────

const serverSchema = z.object({
  // Node / Next.js runtime
  NODE_ENV: z.enum(['development', 'test', 'production']).default('development'),
  NEXT_RUNTIME: z.string().optional(),
  VERCEL_ENV: z.string().optional(),

  // Hasura GraphQL
  HASURA_GRAPHQL_URL: z.string().min(1).optional(),
  HASURA_ADMIN_SECRET: z.string().min(1).optional(),
  HASURA_GRAPHQL_JWT_SECRET: z.string().min(1).optional(),
  HASURA_JWT_KEY: z.string().min(1).optional(),

  // Auth
  AUTH_SECRET: z.string().min(1).optional(),
  JWT_SECRET: z.string().min(1).optional(),

  // MinIO
  MINIO_ENDPOINT: z.string().min(1).optional(),
  MINIO_ACCESS_KEY: z.string().min(1).optional(),
  MINIO_SECRET_KEY: z.string().min(1).optional(),
  MINIO_BUCKET: z.string().min(1).default('uploads'),

  // Upstash Redis
  UPSTASH_REDIS_REST_URL: z.string().min(1).optional(),
  UPSTASH_REDIS_REST_TOKEN: z.string().min(1).optional(),

  // Resend email
  RESEND_API_KEY: z.string().min(1).optional(),
  RESEND_FROM_EMAIL: z.string().min(1).default('noreply@unity.dev'),

  // Alert streaming
  ALERT_TOKEN_SECRET: z.string().min(1).optional(),
  ALERT_STREAM_POLL_MS: z.string().min(1).default('1500'),
  HUB_USE_WS: z.string().optional(),

  // S9.D.06 — Vercel deploy webhook receiver
  VERCEL_WEBHOOK_SECRET: z.string().min(1).optional(),
  SLACK_DEPLOY_WEBHOOK_URL: z.string().url().optional(),

  // S9.H.17 — Admin MFA enforcement
  UNITY_TOTP_ENCRYPTION_KEY: z.string().min(1).optional(),
  ADMIN_MFA_GRACE_WINDOW_DAYS: z.string().min(1).default('14'),
})

const clientSchema = z.object({
  NEXT_PUBLIC_APP_URL: z.string().min(1).default('https://my.unity.dev'),
})

// ─── Validation ───────────────────────────────────────────────────────────────

function parseEnv() {
  const isTest = process.env.NODE_ENV === 'test'

  if (isTest) {
    // In test environment: return process.env passthrough so test files can
    // manipulate process.env directly without hitting Zod validation.
    return {
      // server
      NODE_ENV: process.env.NODE_ENV as 'development' | 'test' | 'production',
      NEXT_RUNTIME: process.env.NEXT_RUNTIME,
      VERCEL_ENV: process.env.VERCEL_ENV,
      HASURA_GRAPHQL_URL: process.env.HASURA_GRAPHQL_URL,
      HASURA_ADMIN_SECRET: process.env.HASURA_ADMIN_SECRET,
      HASURA_GRAPHQL_JWT_SECRET: process.env.HASURA_GRAPHQL_JWT_SECRET,
      HASURA_JWT_KEY: process.env.HASURA_JWT_KEY,
      AUTH_SECRET: process.env.AUTH_SECRET,
      JWT_SECRET: process.env.JWT_SECRET,
      MINIO_ENDPOINT: process.env.MINIO_ENDPOINT,
      MINIO_ACCESS_KEY: process.env.MINIO_ACCESS_KEY,
      MINIO_SECRET_KEY: process.env.MINIO_SECRET_KEY,
      MINIO_BUCKET: process.env.MINIO_BUCKET ?? 'uploads',
      UPSTASH_REDIS_REST_URL: process.env.UPSTASH_REDIS_REST_URL,
      UPSTASH_REDIS_REST_TOKEN: process.env.UPSTASH_REDIS_REST_TOKEN,
      RESEND_API_KEY: process.env.RESEND_API_KEY,
      RESEND_FROM_EMAIL: process.env.RESEND_FROM_EMAIL ?? 'noreply@unity.dev',
      ALERT_TOKEN_SECRET: process.env.ALERT_TOKEN_SECRET,
      ALERT_STREAM_POLL_MS: process.env.ALERT_STREAM_POLL_MS ?? '1500',
      HUB_USE_WS: process.env.HUB_USE_WS,
      VERCEL_WEBHOOK_SECRET: process.env.VERCEL_WEBHOOK_SECRET,
      SLACK_DEPLOY_WEBHOOK_URL: process.env.SLACK_DEPLOY_WEBHOOK_URL,
      UNITY_TOTP_ENCRYPTION_KEY: process.env.UNITY_TOTP_ENCRYPTION_KEY,
      ADMIN_MFA_GRACE_WINDOW_DAYS: process.env.ADMIN_MFA_GRACE_WINDOW_DAYS ?? '14',
      // client (prefixed)
      NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL ?? 'https://my.unity.dev',
    }
  }

  const serverResult = serverSchema.safeParse(process.env)
  const clientResult = clientSchema.safeParse(process.env)

  const errors: string[] = []

  if (!serverResult.success) {
    errors.push(
      ...serverResult.error.issues.map(
        (issue) => `  ${issue.path.join('.')}: ${issue.message}`,
      ),
    )
  }

  if (!clientResult.success) {
    errors.push(
      ...clientResult.error.issues.map(
        (issue) => `  ${issue.path.join('.')}: ${issue.message}`,
      ),
    )
  }

  if (errors.length > 0) {
    throw new Error(
      `[env] Invalid environment variables:\n${errors.join('\n')}`,
    )
  }

  return {
    ...serverResult.data!,
    ...clientResult.data!,
  }
}

// ─── Export ───────────────────────────────────────────────────────────────────

/**
 * Validated environment variables. Import this instead of `process.env`.
 *
 * @example
 *   import { env } from '@/env'
 *   const url = env.HASURA_GRAPHQL_URL
 */
export const env = parseEnv()
